import { useMemo } from 'react';
import { graphql } from 'react-relay';

import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';

import {
	LoadTimeRating,
	type ContentMetricRow,
} from '../content-metrics/components/ContentMetricsTable.tsx';
import type { ContentType } from '../content-metrics/components/filters/TypeFilter.tsx';

import type {
	useContentMetricsQuery,
	ConfluenceContentPerformanceContentType,
	ConfluenceContentPerformanceFilters,
	ConfluenceLoadTimeRating,
} from './__generated__/useContentMetricsQuery.graphql.ts';

const DEFAULT_PAGE_SIZE = 15;
const DEFAULT_DATE_RANGE_DAYS = 7;

// Keep default date range values stable across renders so query variables
// are deterministic and don't trigger accidental refetches.
const DEFAULT_END_TIMESTAMP = new Date().toISOString();
const DEFAULT_START_TIMESTAMP = new Date(
	Date.now() - DEFAULT_DATE_RANGE_DAYS * 24 * 60 * 60 * 1000,
).toISOString();

const CONTENT_TYPE_TO_GQL: Record<ContentType, ConfluenceContentPerformanceContentType> = {
	page: 'PAGE',
	'live-doc': 'LIVE_PAGE',
	blog: 'BLOG_POST',
};

const RATING_TO_GQL: Record<string, ConfluenceLoadTimeRating> = {
	SLOW: 'SLOW',
	MODERATE: 'MODERATE',
	FAST: 'FAST',
};

export type ContentMetricsFilters = {
	types?: ContentType[];
	ratings?: LoadTimeRating[];
	spaceIds?: string[];
	ownerIds?: string[];
};

const buildGraphQLFilters = (
	filters?: ContentMetricsFilters,
): ConfluenceContentPerformanceFilters | undefined => {
	if (!filters) {
		return undefined;
	}

	const gqlFilters: ConfluenceContentPerformanceFilters = {};

	if (filters.types && filters.types.length > 0) {
		gqlFilters.contentType = filters.types.map((t) => CONTENT_TYPE_TO_GQL[t]);
	}

	if (filters.ratings && filters.ratings.length > 0) {
		gqlFilters.rating = filters.ratings.map((r) => RATING_TO_GQL[r]);
	}

	if (filters.spaceIds && filters.spaceIds.length > 0) {
		gqlFilters.spaceId = filters.spaceIds;
	}

	if (filters.ownerIds && filters.ownerIds.length > 0) {
		gqlFilters.ownerId = filters.ownerIds;
	}

	// Return undefined if no filters are actually set
	const hasAnyFilter =
		gqlFilters.contentType || gqlFilters.rating || gqlFilters.spaceId || gqlFilters.ownerId;
	return hasAnyFilter ? gqlFilters : undefined;
};

const mapOperations = (
	operations: ReadonlyArray<
		{ operation?: string | null; targetType?: string | null } | null | undefined
	>,
) =>
	operations.map((operation) => ({
		operation: operation?.operation ?? '',
		targetType: operation?.targetType ?? '',
	}));

const mapLoadTimeRating = (loadTimeRating?: string | null): LoadTimeRating => {
	switch (loadTimeRating) {
		case 'FAST':
			return LoadTimeRating.FAST;
		case 'SLOW':
			return LoadTimeRating.SLOW;
		default:
			return LoadTimeRating.MODERATE;
	}
};

/**
 * Fetches content metrics using Relay GraphQL query.
 *
 * @param searchQuery - Optional search term to filter content by title
 * @returns Object containing data (array of ContentMetricRow)
 *
 * This hook:
 * - Queries the confluence_contentPerformanceList GraphQL endpoint via Relay
 * - Uses last 7 days as the default time window
 * - Supports server-side search filtering via searchTerm parameter
 * - Returns paginated results (first 15 items by default)
 */
export const useContentMetrics = (searchQuery: string, filters?: ContentMetricsFilters) => {
	const { cloudId } = useSessionData();

	// Date range defaults.
	// NOTE: do not compute with `new Date()` here; see module-level constants above.
	const startTimestamp = DEFAULT_START_TIMESTAMP;
	const endTimestamp = DEFAULT_END_TIMESTAMP;

	const gqlFilters = useMemo(
		() => buildGraphQLFilters(filters),
		// Use granular dependencies to avoid recomputation when the filters
		// object reference changes but the individual field values have not.
		// eslint-disable-next-line react-hooks/exhaustive-deps
		[filters?.types, filters?.ratings, filters?.spaceIds, filters?.ownerIds],
	);

	const variables = useMemo(
		() => ({
			cloudId,
			first: DEFAULT_PAGE_SIZE,
			searchTerm: searchQuery?.trim() || undefined,
			filters: gqlFilters,
			startTimestamp,
			endTimestamp,
		}),
		[cloudId, searchQuery, gqlFilters, startTimestamp, endTimestamp],
	);

	const { data, loading, error } = useQueryInterop<
		useContentMetricsQuery['response'],
		useContentMetricsQuery['variables']
	>(
		graphql`
			query useContentMetricsQuery(
				$cloudId: ID!
				$first: Int!
				$after: String
				$searchTerm: String
				$filters: ConfluenceContentPerformanceFilters
				$startTimestamp: String!
				$endTimestamp: String
			) {
				confluence_contentPerformanceList(
					cloudId: $cloudId
					first: $first
					after: $after
					searchTerm: $searchTerm
					filters: $filters
					startTimestamp: $startTimestamp
					endTimestamp: $endTimestamp
				) {
					nodes {
						id
						content {
							contentTitle
							link
							contentId
						}
						loadTimeRating
						medianLoadTime
						views
						space {
							id
							name
						}
						contentOwner {
							id
							displayName
						}
						operations {
							operation
							targetType
						}
					}
					pageInfo {
						hasNextPage
						endCursor
						startCursor
						hasPreviousPage
					}
					totalCount
				}
			}
		`,
		{
			variables,
			skip: !cloudId,
			fetchPolicy: 'network-only',
		},
	);

	const items: ContentMetricRow[] = useMemo(() => {
		const nodes = data?.confluence_contentPerformanceList?.nodes ?? [];

		return nodes.map((node) => ({
			id: node?.id ?? '',
			content: {
				contentTitle: node?.content?.contentTitle ?? '',
				link: node?.content?.link ?? '',
				contentId: node?.content?.contentId ?? '',
			},
			loadTimeRating: mapLoadTimeRating(node?.loadTimeRating),
			medianLoadTime: node?.medianLoadTime ?? 0,
			views: node?.views ?? 0,
			space: {
				id: node?.space?.id ?? '',
				name: node?.space?.name ?? '',
			},
			contentOwner: {
				id: node?.contentOwner?.id ?? '',
				displayName: node?.contentOwner?.displayName ?? '',
			},
			operations: mapOperations(node?.operations ?? []),
			actions: '',
		}));
	}, [data]);

	const spaces = useMemo(() => {
		const seen = new Map<string, string>();
		for (const item of items) {
			if (item.space.id && !seen.has(item.space.id)) {
				seen.set(item.space.id, item.space.name);
			}
		}
		return Array.from(seen, ([id, name]) => ({ id, name }));
	}, [items]);

	const owners = useMemo(() => {
		const seen = new Map<string, string>();
		for (const item of items) {
			if (item.contentOwner.id && !seen.has(item.contentOwner.id)) {
				seen.set(item.contentOwner.id, item.contentOwner.displayName);
			}
		}
		return Array.from(seen, ([id, displayName]) => ({ id, displayName }));
	}, [items]);

	return {
		data: items,
		spaces,
		owners,
		loading,
		error,
	};
};
